import React from 'react';
import { FlaskConical, Cpu, Lightbulb, Target, Users, Wrench, Brain, Rocket, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../context/LanguageContext';

const OpportunitiesPage = () => {
  const { t } = useLanguage();

  const techActivityIcons = [Wrench, Target, Rocket, Brain];

  return (
    <div className="min-h-screen bg-[#060b1a]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b1a] via-[#0a1628] to-[#0f1f33]" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#4DFF88]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-[#4DD6FF]/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-[#FF4DA6]/10 rounded-full blur-[100px]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full border border-[#4DFF88]/30 bg-[#4DFF88]/5 text-[#4DFF88] text-sm font-medium mb-6">
              {t('opportunitiesPage.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
              {t('opportunitiesPage.title')}
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('opportunitiesPage.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* AR-GE Section */}
      <section id="arge" className="relative py-16 bg-[#060b1a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl border border-[#4DFF88]/20 bg-gradient-to-br from-[#4DFF88]/5 to-transparent mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-[#4DFF88]/10 flex items-center justify-center">
                <FlaskConical className="w-8 h-8 text-[#4DFF88]" />
              </div>
              <div>
                <h2 className="text-3xl font-semibold text-white">{t('opportunitiesPage.arge.title')}</h2>
                <p className="text-[#4DFF88]">{t('opportunitiesPage.arge.subtitle')}</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('opportunitiesPage.arge.intro1')}
            </p>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('opportunitiesPage.arge.intro2')}
            </p>

            <h3 className="text-xl font-semibold text-white mb-4">
              {t('header.home') === 'Home' ? 'Program Scope' : 'Program Kapsamı'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {(() => {
                const features = t('opportunitiesPage.arge.features');
                return Array.isArray(features) && features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#4DFF88] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ));
              })()}
            </div>

            <div className="p-4 rounded-xl bg-[#4DFF88]/10 border border-[#4DFF88]/30">
              <p className="text-[#4DFF88] font-medium text-center">
                {t('opportunitiesPage.arge.quote')}
              </p>
            </div>
          </div>

          {/* Tech Innovation Section */}
          <div id="teknoloji" className="p-8 rounded-2xl border border-[#4DD6FF]/20 bg-gradient-to-br from-[#4DD6FF]/5 to-transparent mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-[#4DD6FF]/10 flex items-center justify-center">
                <Cpu className="w-8 h-8 text-[#4DD6FF]" />
              </div>
              <div>
                <h2 className="text-3xl font-semibold text-white">{t('opportunitiesPage.tech.title')}</h2>
                <p className="text-[#4DD6FF]">{t('opportunitiesPage.tech.subtitle')}</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('opportunitiesPage.tech.intro1')}
            </p>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('opportunitiesPage.tech.intro2')}
            </p>

            <h3 className="text-xl font-semibold text-white mb-4">
              {t('header.home') === 'Home' ? 'Focus Areas' : 'Odak Alanları'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {(() => {
                const areas = t('opportunitiesPage.tech.areas');
                return Array.isArray(areas) && areas.map((area, index) => (
                  <div key={index} className="p-3 rounded-lg bg-white/5 text-center">
                    <span className="text-gray-300">{area}</span>
                  </div>
                ));
              })()}
            </div>

            <h3 className="text-xl font-semibold text-white mb-4">
              {t('header.home') === 'Home' ? 'Activities' : 'Faaliyetler'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {(() => {
                const activities = t('opportunitiesPage.tech.activities');
                return Array.isArray(activities) && activities.map((activity, index) => {
                  const IconComponent = techActivityIcons[index];
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                      <IconComponent className="w-5 h-5 text-[#4DD6FF]" />
                      <span className="text-gray-300">{activity}</span>
                    </div>
                  );
                });
              })()}
            </div>

            <h3 className="text-xl font-semibold text-white mb-4">
              {t('header.home') === 'Home' ? 'Skills Gained' : 'Kazanılan Beceriler'}
            </h3>
            <div className="flex flex-wrap gap-3 mb-6">
              {(() => {
                const skills = t('opportunitiesPage.tech.skills');
                return Array.isArray(skills) && skills.map((skill, index) => (
                  <span key={index} className="px-4 py-2 rounded-full bg-[#4DD6FF]/10 text-[#4DD6FF] text-sm font-medium">
                    {skill}
                  </span>
                ));
              })()}
            </div>

            <div className="p-4 rounded-xl bg-[#4DD6FF]/10 border border-[#4DD6FF]/30">
              <p className="text-gray-300 text-center">
                {t('opportunitiesPage.tech.conclusion')}
              </p>
            </div>
          </div>

          {/* Innovative Projects Section */}
          <div id="inovatif" className="p-8 rounded-2xl border border-[#FF4DA6]/20 bg-gradient-to-br from-[#FF4DA6]/5 to-transparent">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-[#FF4DA6]/10 flex items-center justify-center">
                <Lightbulb className="w-8 h-8 text-[#FF4DA6]" />
              </div>
              <div>
                <h2 className="text-3xl font-semibold text-white">{t('opportunitiesPage.innovative.title')}</h2>
                <p className="text-[#FF4DA6]">{t('opportunitiesPage.innovative.subtitle')}</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('opportunitiesPage.innovative.intro1')}
            </p>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('opportunitiesPage.innovative.intro2')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {t('header.home') === 'Home' ? 'Project Development Steps' : 'Proje Geliştirme Adımları'}
                </h3>
                <div className="space-y-3">
                  {(() => {
                    const steps = t('opportunitiesPage.innovative.steps');
                    return Array.isArray(steps) && steps.map((step, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#FF4DA6]/10 flex items-center justify-center text-[#FF4DA6] font-semibold text-sm">
                          {index + 1}
                        </div>
                        <span className="text-gray-300">{step}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {t('header.home') === 'Home' ? 'Project Outcomes' : 'Proje Çıktıları'}
                </h3>
                <div className="space-y-3">
                  {(() => {
                    const outcomes = t('opportunitiesPage.innovative.outcomes');
                    return Array.isArray(outcomes) && outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-[#FF4DA6]" />
                        <span className="text-gray-300">{outcome}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#FF4DA6]/10 border border-[#FF4DA6]/30">
              <p className="text-gray-300 text-center">
                {t('opportunitiesPage.innovative.conclusion')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 bg-[#060b1a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl border border-white/10 bg-white/5 text-center">
            <Sparkles className="w-12 h-12 text-[#4DFF88] mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-4">
              {t('opportunitiesPage.cta.title')}
            </h3>
            <p className="text-gray-400 mb-6">
              {t('opportunitiesPage.cta.description')}
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#4DFF88] hover:bg-[#4DFF88]/10 hover:text-[#4DFF88] text-[#060b1a] font-medium rounded-none transition-all duration-300"
            >
              {t('opportunitiesPage.cta.button')}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OpportunitiesPage;
